/**
 * Observed daily weather straight from Open-Meteo's public archive.
 *
 * Called from the browser rather than through our API on purpose: this is a
 * dev-only QA aid, the archive needs no key and allows CORS, and a server
 * endpoint would be a route to maintain for a tool that shows a number.
 *
 * The variables match `SHARED_DAILY_VARIABLES` in the insights service, so what
 * is shown here is what the forecast actually learns from.
 */

const ARCHIVE_URL = "https://archive-api.open-meteo.com/v1/archive";

/** The archive reanalysis lags a few days; anything newer comes back null. */
export const ARCHIVE_LAG_DAYS = 5;

export interface ObservedDay {
  date: string;
  weatherCode: number | null;
  tempMaxC: number | null;
  rainMm: number | null;
  sunshineHours: number | null;
  windKph: number | null;
}

export async function fetchObservedWeather(params: {
  latitude: number;
  longitude: number;
  from: string;
  to: string;
}): Promise<Record<string, ObservedDay>> {
  const query = new URLSearchParams({
    latitude: String(params.latitude),
    longitude: String(params.longitude),
    start_date: params.from,
    end_date: params.to,
    timezone: "auto",
    temperature_unit: "celsius",
    wind_speed_unit: "kmh",
    precipitation_unit: "mm",
    daily: [
      "weather_code",
      "temperature_2m_max",
      "precipitation_sum",
      "sunshine_duration",
      "wind_speed_10m_max",
    ].join(","),
  });

  const response = await fetch(`${ARCHIVE_URL}?${query.toString()}`);
  if (!response.ok) throw new Error(`Open-Meteo responded ${response.status}`);

  const body = await response.json();
  const dates: string[] = body?.daily?.time ?? [];
  const byDate: Record<string, ObservedDay> = {};

  dates.forEach((date, index) => {
    const seconds = readValue(body?.daily?.sunshine_duration, index);
    byDate[date] = {
      date,
      weatherCode: readValue(body?.daily?.weather_code, index),
      tempMaxC: readValue(body?.daily?.temperature_2m_max, index),
      rainMm: readValue(body?.daily?.precipitation_sum, index),
      sunshineHours:
        seconds === null ? null : Math.round((seconds / 3600) * 10) / 10,
      windKph: readValue(body?.daily?.wind_speed_10m_max, index),
    };
  });

  return byDate;
}

/** A wet day at or above this reads as rain, matching the tile's own rule. */
export const WET_DAY_MM = 1;

export function describeObserved(day?: ObservedDay): string {
  if (!day) return "no reading";

  const parts: string[] = [];
  if (day.tempMaxC !== null) parts.push(`${Math.round(day.tempMaxC)}°C`);
  if (day.rainMm !== null) {
    parts.push(day.rainMm >= WET_DAY_MM ? `${day.rainMm}mm rain` : "dry");
  }
  if (day.sunshineHours !== null) parts.push(`${day.sunshineHours}h sun`);

  return parts.join(" · ") || "no reading";
}

/** A threshold-triggered multiplier: `multiplier` applies once the reading crosses `threshold`. 1.0 = no effect. */
export interface WeatherFactor {
  threshold: number;
  multiplier: number;
}

/** Mirrors the forecast model's own daily features, so every one is teachable. */
export interface WeatherFactors {
  /** Fires when `rainMm` is at or above the threshold. */
  rain: WeatherFactor;
  /** Fires when `sunshineHours` is below the threshold. */
  sun: WeatherFactor;
  /** Fires when `tempMaxC` is at or above the threshold. */
  warm: WeatherFactor;
  /** Fires when `windKph` is at or above the threshold. */
  wind: WeatherFactor;
}

/** Reproduces the tool's original hardcoded rule exactly; wind is new and starts neutral. */
export const DEFAULT_WEATHER_FACTORS: WeatherFactors = {
  rain: { threshold: WET_DAY_MM, multiplier: 0.75 },
  sun: { threshold: 2, multiplier: 0.9 },
  warm: { threshold: 24, multiplier: 1.15 },
  wind: { threshold: 30, multiplier: 1 },
};

/**
 * A deliberate, documented weather-to-revenue rule.
 *
 * Only for seeding: it manufactures a relationship the model can be tested
 * against. A good validation score partly measures this rule rather than real
 * merchant behaviour, which is the point and also the limit.
 */
export function weatherMultiplier(
  day: ObservedDay | undefined,
  factors: WeatherFactors
): number {
  if (!day) return 1;

  const rain =
    (day.rainMm ?? 0) >= factors.rain.threshold ? factors.rain.multiplier : 1;
  const sun =
    (day.sunshineHours ?? 99) < factors.sun.threshold
      ? factors.sun.multiplier
      : 1;
  const warm =
    (day.tempMaxC ?? 0) >= factors.warm.threshold ? factors.warm.multiplier : 1;
  const wind =
    (day.windKph ?? 0) >= factors.wind.threshold ? factors.wind.multiplier : 1;

  return Math.round(rain * sun * warm * wind * 100) / 100;
}

function readValue(series: unknown, index: number): number | null {
  const value = Array.isArray(series) ? series[index] : undefined;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
